import { BUILDER_END, PERMIT_KEY, holdsList, type PhaseKey, ownListOffers, decisionChoice, keptWords, stepCalled, type ProcessKept, phasesOf, PERFORMER_KIND_WORDS, STEP_KIND_WORDS, TASK_OWNERS, formatMoney, planDisable, type ProcessStepDto, type TaskOwner, titleCase } from '@nsb/core';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ApiError, errorText } from '../../api.ts';
import { Button, ErrorLine, TextInput } from '../ui.tsx';
import { MoneyInput } from '../MoneyInput.tsx';
import { DecisionChoice } from '../DecisionChoice.tsx';
import { chooseWords, chosenWords } from '../decisionWords.ts';
import { span } from './span.ts';
import { dayBounds, dayByHand, dayRefusal } from './dayBounds.ts';
import { BuildingTypes } from './BuildingTypes.tsx';
import { StepCard } from './StepCard.tsx';
import { LatestQuote, StepLog, TaskLogCount } from './StepLog.tsx';
import { LogCounts, useStepLog } from './useStepLog.ts';
import { logCounts } from './stepLog.ts';
import { TradeOptions } from './TradeOptions.tsx';
import { LateReason } from './LateReason.tsx';
import { lateWords } from './lateWords.ts';
import { processAction, useTrades, type ProcessModel } from './useProcess.ts';
import { gateDone } from '../tree/passed.ts';
import { NeedsOpens } from './NeedsOpens.tsx';
import { cardBoard, jobName, jobNeedsOpens, jobWalk, nextInWalk, type PanelBoard, type Side } from './needsOpens.ts';
import { dateWords, easternToday } from '../../days.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { useApi } from '../../api.ts';
import { tasksCount } from './taskCount.ts';
import { TaskGroups } from './TaskGroups.tsx';
import type { TaskDrag, TaskGroup } from './taskGroups.ts';
import { stepReopen, taskToggle } from './taskToggle.ts';
import { clientTaskStanding, fullStop, heldWords, isClientTask, listedAboveOpen, tasksWords } from './holdWords.ts';
import { RecordApproval, RecordedApproval } from './ApprovalRecord.tsx';
import { recordedWords } from './approvalWords.ts';
import { RemindAsk, RemindLink } from '../Remind.tsx';
import { useRemind } from '../useRemind.ts';
import { toldLine } from '../remindWords.ts';
import type { ReviewSetDto } from '@nsb/core';

/**
 * One step, opened: what it is and why, where it stands, when it lands, who
 * does it, what it waits on; and every change to it (state, a gate or
 * inspection passed or overridden, a date moved, people, links, turning it
 * off), and its own tasks, each with its controls. A package also carries its
 * trade, its performer, its scope note and its quote. A locked step shows what
 * unlocking it brings.
 */

/**
 * How much of the job's process is kept as it ended (L-19 A; core `processKept`, sent as `ProcessDto.kept`): completed,
 * its shape (nothing to add, file away or redraw here); archived, the whole of it, so a step opened reads only: where it
 * stands in words, nothing to press (design study 24).
 */
const Kept = createContext<ProcessKept | null>(null);
/** An archived job's panel: every control left out, its words kept. */
const useReadOnly = () => useContext(Kept) === 'whole';

/** The line an archived job's step says under where it stands. */
function KeptLine({ model }: { model: ProcessModel }) {
  if (!useReadOnly()) return null;
  return <p className="mt-2 text-sm text-ink-2">{keptWords('whole', model.dto.tree).panel}</p>;
}

const OWNER_WORDS: Record<TaskOwner, string> = { nsb: 'NSB', client: 'The client', sub: 'A sub', arch: 'The engineer', vendor: 'A supplier', authority: 'The inspector' };
const KIND_WORDS = STEP_KIND_WORDS;
const long = (d: string) => dateWords(d, 'date');

function useRun(projectId: string) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const run = async (path: string, body: unknown = {}, method?: string): Promise<boolean> => {
    setBusy(true);
    setErr(null);
    setCode(null);
    try {
      await processAction(projectId, path, body, method);
      return true;
    } catch (e) {
      setErr(errorText(e));
      setCode(e instanceof ApiError ? e.code : null);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, code, run, clear: () => setErr(null) };
}

/**
 * What a step needs and what it opens, setting the job up (design study 31, slice 5; NeedsOpens), from the job's links,
 * each change into the job's draft as every change here is. A wait on a gate or an inspection not passed holds the work
 * back: it is taken out only with a reason, which is on the record, from either list (taking out what a gate opens is
 * taking out a wait on that gate); a gate after a gate keeps the gates' order. + It needs… and + It opens… choose the step
 * on the board, or by its name, as the page draws it (`board.onChoose`, JobTree's), which sends the line.
 */
function SetupLines({ projectId, model, step: s, only, board }: { projectId: string; model: ProcessModel; step: ProcessStepDto; only?: Side; board?: PanelBoard }) {
  const { busy, err, run } = useRun(projectId);
  const phases = board?.phases;
  const steps = board?.steps;
  const lines = board?.lines;
  // The board as the setup page draws it, its rows worked out once for its tiles.
  const drawing = useMemo(() => (phases && steps && lines ? cardBoard(phases, steps, lines) : undefined), [phases, steps, lines]);
  const rows = useMemo(() => jobNeedsOpens(model, s, drawing), [model, s, drawing]);
  const walk = useMemo(() => jobWalk(model), [model]);
  const nextId = board ? nextInWalk(walk, s.id) : null;
  const next = nextId ? model.step(nextId) : undefined;
  /** The wait a line of either list is: what it needs, it waits on; what it opens waits on it. */
  const wait = (side: Side, other: string) => (side === 'needs' ? { stepId: s.id, needsId: other } : { stepId: other, needsId: s.id });
  const choose = board?.onChoose;
  return (
    <div className="border-t border-rule py-3">
      <NeedsOpens
        step={jobName(s)}
        rows={rows}
        only={only}
        busy={busy}
        onTakeOut={(side, other, reason) => run('/links', { ...wait(side, other), on: false, ...(reason ? { reason } : {}) })}
        // Chosen on the board, or by its name, as the page draws it (TreeView's `choosing`); the line is the page's to send.
        onAdd={choose ? (side) => choose(s.id, side) : undefined}
        next={next && board ? { name: jobName(next), onGo: () => board.onOpen(next.id) } : null}
        onOpen={board?.onOpen}
      />
      <ErrorLine error={err} />
    </div>
  );
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-t border-rule py-3">
      <div className="label mb-1.5">{label}</div>
      {children}
    </div>
  );
}

/**
 * Held (design study 24, L-17 B): the nearest gate or inspection not passed before it, however far back, and the way past
 * it, as the API refuses it. Nothing while nothing holds it.
 */
function Held({ model, step: s, inList = false }: { model: ProcessModel; step: ProcessStepDto; inList?: boolean }) {
  const held = heldWords(model, s);
  if (!held) return null;
  const body = (
    <>
      <p className="text-sm">{held.line}</p>
      <p className="meta mt-1">{held.way}</p>
    </>
  );
  return inList ? <div className="mb-2">{body}</div> : <Block label="Held">{body}</Block>;
}

/** A small pencil beside a date, changing it (a 44px target round it, `.hit`). */
function EditMark({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} className="hit inline-flex h-5 w-5 items-center justify-center text-ink-3 hover:text-ink">
      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" className="fill-none stroke-current" strokeWidth={1.3} strokeLinejoin="round">
        <path d="M10.8 2.6l2.6 2.6L6 12.6 2.8 13.2l.6-3.2z M9.4 4l2.6 2.6" />
      </svg>
    </button>
  );
}

/**
 * The day a step went under way, or was done, with a pencil to set it after the fact (Jason, 2026-10-07: "a little
 * edit icon next to the when date allowing for overriding retroactively, but you can't mark something done at a date
 * in the future"): never a day still to come, never done before a start set apart, never under way after it was done
 * (`dayBounds`, the API's rule); a day it cannot take is refused in words beside Save, never by a Save held back (P-23).
 * A gate NSB marked done has its pencil too, a gate the client approved never (`dayByHand`, P-32); `line` words the day
 * in a sentence of its own (a gate's, as `gateDone` says it), the pencil after it.
 */
function DayLine({ projectId, step: s, field, words, line }: { projectId: string; step: ProcessStepDto; field: 'startedOn' | 'doneOn'; words: string; line?: string }) {
  const day = s[field];
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(day ?? '');
  const [refused, setRefused] = useState<string | null>(null);
  const { busy, err, run } = useRun(projectId);
  // Only an admin changes these days (Jason, 2026-10-07), and never on an archived job (L-19 A).
  const readOnlyDays = useReadOnly();
  const me = useMe().data;
  const admin = !readOnlyDays && isAdmin(me) && dayByHand(s, field);
  if (!day) return line ? <>{line}</> : null;
  const today = easternToday();
  const { min, max } = dayBounds(s, field, today);
  const what = field === 'startedOn' ? 'went under way' : s.kind === 'milestone' ? 'was reached' : 'was done';
  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        {line ?? `${words} ${long(day)}`}
        {admin ? (
          <EditMark
            label={`Change the day it ${what}`}
            onClick={() => {
              setValue(day);
              setRefused(null);
              setEditing(true);
            }}
          />
        ) : null}
      </span>
    );
  }
  const save = () => {
    const why = dayRefusal(s, field, value, today);
    setRefused(why);
    if (!why) void run(`/steps/${s.id}`, { [field]: value }, 'PATCH').then((ok) => ok && setEditing(false));
  };
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <label className="inline-flex items-center gap-2">
        <span>The day it {what}</span>
        <input
          type="date"
          className="field w-auto py-1"
          value={value}
          max={max}
          min={min}
          onChange={(e) => {
            setValue(e.target.value);
            setRefused(null);
          }}
        />
      </label>
      <Button tone="quiet" busy={busy} disabled={!value} onClick={save}>
        Save
      </Button>
      <Button tone="quiet" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      <ErrorLine error={refused ?? err} />
    </span>
  );
}

/**
 * When it went under way and when it was done, under where it stands (Jason, 2026-10-07: "a little audit trail under
 * where it stands showing the date it went underway, and date for done"), each changed after the fact with its pencil.
 */
function StandingDays({ projectId, step: s }: { projectId: string; step: ProcessStepDto }) {
  if (!s.startedOn && !s.doneOn) return null;
  return (
    <ul className="mt-2 grid gap-0.5 text-sm text-ink-2">
      {s.startedOn ? (
        <li>
          <DayLine projectId={projectId} step={s} field="startedOn" words="Under way" />
        </li>
      ) : null}
      {s.doneOn ? (
        <li>
          <DayLine projectId={projectId} step={s} field="doneOn" words={s.kind === 'milestone' ? 'Reached' : 'Done'} />
        </li>
      ) : null}
    </ul>
  );
}

/** A block of the panel, or, for a task in a step's list, the same controls under its row. */
function Frame({ label, inList, children }: { label: string; inList: boolean; children: ReactNode }) {
  return inList ? <div className="mt-1.5 rounded-[2px] bg-inset/50 p-3">{children}</div> : <Block label={label}>{children}</Block>;
}

export function StepPanel({
  projectId,
  model,
  stepId,
  onClose,
  onTurnedOn,
  setup = true,
  board,
}: {
  projectId: string;
  model: ProcessModel;
  stepId: string;
  onClose: () => void;
  /** Setting the job's process up: what it needs and opens is changed here, and it is taken out; running it, those are setup's. */
  setup?: boolean;
  /** A step this one unlocked was turned on from here: the editor shows what that one could lead to next. */
  onTurnedOn?: (key: string) => void;
  /** Setting it up, the board beside it: what it draws, and another step opened from here. */
  board?: PanelBoard;
}) {
  const s = model.step(stepId);
  const { busy, err, code, run } = useRun(projectId);
  // The options picked deciding it, for the step they were picked on (design study 22).
  const [pick, setPick] = useState<{ stepId: string; ids: string[] } | null>(null);
  // Its log (design study 31, slice 3), on the working panel alone: read while it is open; a task's count shows its own.
  const logged = !setup && Boolean(s && !s.off && !isStarter(model, s));
  const { data: log } = useStepLog(projectId, stepId, logged);
  const [logFor, setLogFor] = useState<string | null>(null);
  if (!s || s.off) return null;
  // A decision with options is decided by choosing: one of them, or any, as it says; Done carries the choice. What was
  // chosen reads on it once it is decided, even after the master took its options away.
  const decision = s.kind === 'decision' && s.decision && (s.decision.options.length || s.decision.chosen) ? s.decision : null;
  const picked = pick?.stepId === s.id ? pick.ids : (decision?.chosen?.options.map((o) => o.id).filter((id) => decision.options.some((o) => o.id === id)) ?? []);
  const choosing = decision !== null && decision.options.length > 0 && s.state !== 'done';
  const chosenEnough = !choosing || !('refused' in decisionChoice({ title: s.title, options: decision.options, choose: decision.choose }, picked));
  const t = model.schedule.timing.get(s.id);
  // The end date is one someone set only when the step the job ends with is dated.
  const end = model.schedule.end ? model.schedule.timing.get(model.schedule.end.id) : undefined;
  const endDated = Boolean(end?.dated);
  const live = model.dto.steps.filter((x) => !x.off);
  const waits = model.dto.links.filter((l) => l.stepId === s.id).map((l) => live.find((x) => x.id === l.needsId)).filter((x): x is ProcessStepDto => Boolean(x));
  const before = model.dto.links.filter((l) => l.needsId === s.id).map((l) => live.find((x) => x.id === l.stepId)).filter((x): x is ProcessStepDto => Boolean(x));
  const inside = live.filter((x) => x.parentId === s.id);
  const gate = s.gate;
  // Held by a gate or inspection not passed before it (L-17 B): it neither goes under way nor is done until that is.
  const held = Boolean(heldWords(model, s));
  // Done only once every task in it is (L-23): what it waits on, the client's counted as theirs.
  const waitingOn = tasksWords(model, s);
  // Kept as it ended (L-19 A): completed, nothing of its shape changes here; archived, nothing at all.
  const kept = model.dto.kept ?? null;
  const readOnly = kept === 'whole';
  const shaping = setup && !kept;
  // A step that came from the master, set up here: its card, with the master's fields and switches (design study 25).
  const card = shaping && s.follows !== undefined;
  // Done on a completed job, it stays done (development report L-30; Jason, 2026-10-08): nothing takes it back.
  const stays = kept === 'shape' && s.state === 'done';
  // A step carrying a pattern language is done by its lock alone (spec "Pattern Mapping" §4.5): never by hand while it is
  // open; locked and done, it stays done until the language is opened again, on Patterns, which a job kept as it ended
  // never is.
  const language = (s.specials ?? []).find((x) => x.key === 'patterns');
  const languageOpen = Boolean(language && !language.done);
  const lockedDone = Boolean(language?.done) && s.state === 'done';
  // The start (P-21, Jason, 2026-10-09: "it really just is a starter node, it can't have people attached, it cant
  // depend on anything, it has no due date"): where the job begins, always reached; no standing to set, no days, no
  // people, nothing it waits on, never filed away. Its words stay its own.
  const starter = isStarter(model, s);
  // A task is added only where a list is held (core `holdsList`: work, a decision or a sign-off, as the engine refuses
  // the rest), never on the job's start or its end (slice 4's final review).
  const listed = shaping && holdsList(s.kind) && !starter && s.key !== BUILDER_END;
  return (
    <Kept.Provider value={kept}>
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="label">
            {s.trade ? 'Package' : KIND_WORDS[s.kind]}
            {s.key === null ? ' · this project’s own' : ''}
            {s.instance ? ` · ${s.instance}` : ''}
          </span>
          <button type="button" className="link min-h-11 text-sm text-ink-3 hover:text-ink" onClick={onClose}>
            Close
          </button>
        </div>
        {/* A step of the master on the setup page: its card holds its title and what it is (design study 25). */}
        {card ? <h2 className="mt-1 text-[1.5rem] leading-tight">{titleCase(s.title)}</h2> : <Title step={s} projectId={projectId} />}
        {s.why && !card ? <p className="mt-2 text-ink-2">{s.why}</p> : null}
        {logged && log ? <LatestQuote projectId={projectId} entries={log.entries} /> : null}
        {gate ? <p className="mt-2 text-sm">It holds back what comes after it in this job until it is done.</p> : null}
        {/* What is being built, on a step carrying Pattern mapping (spec "Pattern Mapping" §4.2): ticked here while setting
            up, first, as what leaves the step not configured until it is. */}
        <BuildingTypes projectId={projectId} step={s} edits={shaping} kept={kept !== null} />
        {card ? <StepCard projectId={projectId} model={model} step={s} /> : null}
        <Deviation projectId={projectId} model={model} step={s} setup={setup} />
        {gate || s.kind === 'inspection' || readOnly || starter ? null : <Held model={model} step={s} />}

        {/* Where it stands */}
        {starter ? (
          <Block label="Where it stands">
            <p className="text-sm">{STARTER_WORDS}</p>
          </Block>
        ) : gate ? (
          <GateControls projectId={projectId} model={model} step={s} />
        ) : s.kind === 'inspection' ? (
          <InspectionControls projectId={projectId} model={model} step={s} />
        ) : (
          <Block label="Where it stands">
            {s.review && s.state !== 'done' ? (
              <p className="mb-1 text-sm">
                Done when its design review is: once everyone asked has finished reviewing it on{' '}
                <Link to={`/p/${projectId}/reviews`} className="link">
                  Design reviews
                </Link>
                .
              </p>
            ) : null}
            {languageOpen && s.state !== 'done' ? (
              <p className="mb-1 text-sm">
                Done when its pattern language is locked, on{' '}
                <Link to={`/p/${projectId}/patterns`} className="link">
                  Patterns
                </Link>
                .
              </p>
            ) : null}
            {lockedDone && !kept ? (
              <p className="mb-1 text-sm">
                Done: its pattern language is locked. Opened again on{' '}
                <Link to={`/p/${projectId}/patterns`} className="link">
                  Patterns
                </Link>
                , with a reason, it goes back under way.
              </p>
            ) : null}
            {decision ? <Decided decision={decision} done={s.state === 'done'} picked={picked} busy={busy} onPick={(ids) => setPick({ stepId: s.id, ids })} /> : null}
            {readOnly ? (
              // Its days say where it stands once it went under way; before that, the word.
              s.startedOn || s.doneOn ? null : <p className="text-sm">{standingWord(s)}</p>
            ) : s.state === 'done' ? (
              // Done, the same quiet grey Reopen as a task's in place of its states, back under way (P-31).
              <div className="mt-1">
                <button
                  type="button"
                  disabled={busy || stays || lockedDone}
                  title={stays ? keptWords('shape', model.dto.tree).undone : undefined}
                  onClick={() => void run(`/steps/${s.id}`, { state: stepReopen({ milestone: s.kind === 'milestone', held }).to }, 'PATCH')}
                  className={stepReopen({ milestone: s.kind === 'milestone', held }).look}
                >
                  Reopen
                </button>
              </div>
            ) : (
              <div className="mt-1 flex flex-wrap gap-2" role="group" aria-label="State">
                {(s.kind === 'milestone'
                  ? ([
                      ['not_started', 'Not yet'],
                      ['done', 'Reached'],
                    ] as const)
                  : ([
                      ['not_started', 'Not started'],
                      ['in_progress', 'Under way'],
                      ['done', 'Done'],
                    ] as const)
                )
                  .filter(([state]) => state !== 'done' || (!s.review && !languageOpen))
                  .map(([state, word]) => {
                    // Held, it neither goes under way nor is done (L-17 B); with a task not done, it is not done (L-23).
                    const barred = s.state !== state && ((state === 'done' && (!chosenEnough || waitingOn !== null)) || (held && state !== 'not_started'));
                    return (
                      <button
                        key={state}
                        type="button"
                        aria-pressed={s.state === state}
                        disabled={busy || barred}
                        onClick={() => void run(`/steps/${s.id}`, state === 'done' && choosing ? { state, choice: picked } : { state }, 'PATCH')}
                        className={`min-h-10 rounded-[2px] border px-3 text-[0.8125rem] transition-colors duration-[120ms] ${s.state === state ? 'border-ink bg-ink text-paper' : 'border-rule-strong text-ink-2 hover:border-ink hover:text-ink'} ${barred ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        {word}
                      </button>
                    );
                  })}
              </div>
            )}
            <StandingDays projectId={projectId} step={s} />
            {stays ? <p className="mt-2 text-sm text-ink-2">{keptWords('shape', model.dto.tree).undone}</p> : null}
            {waitingOn && !readOnly ? <p className="mt-2 text-sm text-alert">{waitingOn}</p> : null}
            <Refused err={err} code={code} />
            {s.state !== 'done' && !s.review && !languageOpen ? <Settle projectId={projectId} step={s} /> : null}
            <KeptLine model={model} />
          </Block>
        )}
        <LogCounts.Provider value={logged && log ? { counts: logCounts(log.entries), show: setLogFor } : null}>
          <Tasks projectId={projectId} model={model} holder={s} edits={listed} />
        </LogCounts.Provider>
        {logged ? <StepLog projectId={projectId} step={s} tasks={inside} entries={log?.entries ?? []} forTaskId={logFor} onShowAll={() => setLogFor(null)} /> : null}

        {/* When */}
        {t && !starter ? (
          <Block label="When">
            <p className="text-sm">
              {s.state === 'done' && s.doneOn ? (
                // A gate's or an inspection's with its pencil too, but for a gate the client approved (P-32).
                <DayLine projectId={projectId} step={s} field="doneOn" words={s.kind === 'milestone' ? 'Reached' : 'Done'} />
              ) : (
                t.dated ? (
                  <>
                    {s.kind === 'gate' || s.kind === 'milestone' ? `About ${long(t.finish)}` : `About ${long(t.start)} to ${long(t.finish)}`}
                    {t.critical ? <span className="text-ink"> · on the critical path</span> : t.slackDays > 0 ? <span className="text-ink-3"> · {span(t.slackDays) ?? `${t.slackDays} d`} to spare</span> : null}
                  </>
                ) : (
                  // Only a date someone set (Jason, 2026-10-06): a time on the job, for it and the steps before it, or a plan.
                  <span className="text-ink-3">Not dated: it needs a time on the job, as do the steps before it, or a planned date.</span>
                )
              )}
            </p>
            {readOnly ? null : t.dated && endDated && t.latestStart && t.latestStart <= model.dto.today && s.state !== 'done' && s.kind !== 'gate' && s.kind !== 'milestone' ? (
              <p className="mt-1 text-sm text-alert">Start it now to hold the end date.</p>
            ) : t.dated && endDated && t.latestStart && s.state !== 'done' && s.kind !== 'gate' && s.kind !== 'milestone' ? (
              <p className="meta mt-1">Start by {long(t.latestStart)} to hold the end date</p>
            ) : null}
            {t.atRisk && !readOnly ? <p className="mt-1 text-sm text-alert">The work before it runs past its planned date.</p> : null}
            {!inside.length && s.kind !== 'gate' && s.kind !== 'milestone' ? <Days projectId={projectId} step={s} /> : null}
            {(s.kind === 'gate' || s.kind === 'milestone') && s.state !== 'done' ? <PlanDate projectId={projectId} step={s} expected={t.finish} /> : null}
          </Block>
        ) : null}

        {!gate && s.kind === 'work' ? <Package projectId={projectId} step={s} card={card} /> : null}
        {!gate ? <Words projectId={projectId} step={s} card={card} /> : null}

        {/* The order of work: setting the job up, what it needs and what it opens (design study 31, slice 5); running
            it, what it waits on, read only. */}
        {shaping ? (
          <SetupLines projectId={projectId} model={model} step={s} only={starter ? 'opens' : s.key === BUILDER_END ? 'needs' : undefined} board={board} />
        ) : starter ? (
          before.length ? (
            <Block label="Comes before">
              <p className="text-sm">{before.map((b) => titleCase(b.title)).join(', ')}</p>
            </Block>
          ) : null
        ) : (
          <Block label="Waits on">
            {waits.length ? (
              <ul className="space-y-1">
                {waits.map((w) => (
                  <li key={w.id} className="break-words text-sm">
                    {titleCase(w.title)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-3">Nothing at this level{s.parentId ? ': what holds it waits for it' : ''}.</p>
            )}
            {before.length ? <p className="meta mt-1">Comes before {before.map((b) => titleCase(b.title)).join(', ')}</p> : null}
            <ErrorLine error={code !== 'gate_closed' && code !== 'inspection_open' ? err : null} />
          </Block>
        )}

        {shaping && s.key && !s.instance ? <CanBePartOfIt projectId={projectId} model={model} stepKey={s.key} onTurnedOn={onTurnedOn} /> : null}

        {shaping && !starter ? <TurnOff projectId={projectId} model={model} step={s} onDone={onClose} /> : null}
      </div>
    </Kept.Provider>
  );
}

/** What the start's panel says of where it stands (P-21). */
const STARTER_WORDS = 'Where the job begins: always reached, with no date of its own, no one attached and nothing it waits on.';

/** The job's start, the tree's first step (P-21). */
const isStarter = (model: ProcessModel, s: ProcessStepDto): boolean => s.key !== null && s.key === (model.dto.tree.start ?? null) && !s.parentId;

/** Where a step stands in a word, as its buttons would say it, for a panel that reads only (L-19 A). */
function standingWord(s: ProcessStepDto): string {
  if (s.kind === 'milestone') return s.state === 'done' ? 'Reached' : 'Not yet';
  if (s.kind === 'decision') return s.state === 'done' ? 'Decided' : 'Not decided';
  return s.state === 'done' ? 'Done' : s.state === 'in_progress' ? 'Under way' : 'Not started';
}

function Title({ step: s, projectId }: { step: ProcessStepDto; projectId: string }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(s.title);
  const { busy, err, run } = useRun(projectId);
  const readOnly = useReadOnly();
  if (!editing) {
    return (
      <h2 className="mt-1 text-[1.5rem] leading-tight">
        {titleCase(s.title)}{' '}
        {s.kind !== 'gate' && !readOnly ? (
          <button type="button" className="link align-middle text-sm text-ink-3 hover:text-ink" onClick={() => (setTitle(s.title), setEditing(true))}>
            Rename
          </button>
        ) : null}
      </h2>
    );
  }
  return (
    <div className="mt-1">
      <TextInput aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="mt-2 flex gap-4">
        <Button tone="secondary" busy={busy} disabled={!title.trim()} onClick={() => void run(`/steps/${s.id}`, { title: title.trim() }, 'PATCH').then((ok) => ok && setEditing(false))}>
          Save
        </Button>
        <Button tone="quiet" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

/**
 * A deviation on a step (design studies 20 and 21; Jason's answers, 2026-10-07): a step come late, into the phase under
 * way, or a gate marked done before the rest of its phase. NSB's reason, when and by whom, and the client's answer:
 * waiting on them (the step held, the gate not done, until they confirm it), confirmed (an open exception until it is
 * done; on NSB's own job the reason confirms it), or declined (a step come late stays held: NSB files it away, or
 * proposes it again with a new reason; a gate stays not done).
 */
function Deviation({ projectId, model, step: s, setup }: { projectId: string; model: ProcessModel; step: ProcessStepDto; setup: boolean }) {
  const { busy, err, run } = useRun(projectId);
  const [again, setAgain] = useState(false);
  const [reason, setReason] = useState('');
  const readOnly = useReadOnly();
  // Completed, its shape is kept (L-19 A): a step is no longer filed away, only proposed again.
  const shapeKept = useContext(Kept) !== null;
  const d = s.deviation ?? null;
  // Who signs in to the job as its client (L-20 B): while nobody does, NSB answers for them; once someone does, only they.
  // Not sent (an older API), it waits on the client as before.
  const signIns = model.dto.clientSignIns ?? null;
  // Waiting on them: who was told of it, and Remind them (design study 26).
  const remind = useRemind(projectId, 'deviation', d?.id ?? null, { staff: !readOnly && d?.answer === 'waiting' && Boolean(signIns?.length) });
  if (!s.late && !d) return null;
  const phase = (k: string | null) => (k ? (phasesOf(model.dto.tree).find((p) => p.key === k)?.name ?? k) : 'its phase');
  const late = d ? d.kind === 'late' : true;
  const own = model.dto.kind === 'internal';
  return (
    <Block label={late ? 'Come late' : 'Marked done early'}>
      <p className="text-sm">
        {late
          ? `It came late, into ${phase(s.phase)}: ${phase(s.late?.from ?? null)}, its own phase, was done.`
          : `Marked done before the rest of ${phase(s.phase)} was done.`}
      </p>
      {d ? (
        <>
          <p className="mt-1 text-sm">NSB’s reason: “{d.reason}”</p>
          <p className="meta">
            {d.proposedBy ? `${d.proposedBy}, ` : ''}
            {dateWords(d.proposedAt, 'date')}
          </p>
          {d.answer === 'waiting' && !late && !model.holds.get(s.id)?.length ? (
            // The rest of its phase done since: NSB marks it done as any gate is (the client's confirming it still would).
            <p className="mt-1 text-sm">The rest of {phase(s.phase)} is done now: mark it done as any gate is.</p>
          ) : d.answer === 'waiting' ? (
            <>
              <p className="mt-1 text-sm text-alert">
                Waiting on {signIns?.length ? `${names(signIns)} to confirm it on their Waiting on you` : 'the client to confirm it'}: {late ? 'held until they do, it cannot start.' : 'not done until they do.'}
              </p>
              {readOnly || !signIns ? null : signIns.length ? (
                <>
                  <p className="meta mt-1">
                    {names(signIns)} {signIns.length === 1 ? 'signs' : 'sign'} in to this job, so only they confirm it.
                    {remind.people.length ? ` ${toldLine(remind.people, remind.today)}.` : null}
                  </p>
                  <RemindLink r={remind} />
                  <RemindAsk r={remind} />
                </>
              ) : (
                <ForClient projectId={projectId} deviationId={d.id} />
              )}
            </>
          ) : d.answer === 'confirmed' ? (
            <p className="mt-1 text-sm">
              {own ? 'Confirmed: NSB’s own job, the reason is enough' : d.forClient ? `Confirmed for the client${d.answeredBy ? ` by ${d.answeredBy}` : ''}` : 'Confirmed by the client'}
              {d.answeredAt ? ` · ${dateWords(d.answeredAt, 'date')}` : ''}
              {d.note ? `: “${d.note}”${fullStop(d.note)}` : '.'}{' '}
              {d.open ? <span className="text-ink-2">An open exception until {late ? 'it is done' : `the rest of ${phase(s.phase)} is done`}.</span> : null}
            </p>
          ) : (
            <div className="mt-1 text-sm">
              <p className="text-alert">
                {d.forClient ? `Declined for the client${d.answeredBy ? ` by ${d.answeredBy}` : ''}` : 'Declined by the client'}
                {d.answeredAt ? ` · ${dateWords(d.answeredAt, 'date')}` : ''}
                {d.note ? `: “${d.note}”${fullStop(d.note)}` : '.'}
              </p>
              {late && !readOnly ? (
                <>
                  <p className="mt-1">
                    {shapeKept ? 'It stays held: propose it again with a new reason.' : `It stays held: file it away${setup ? ' (Turn it off, below)' : ', in the project’s settings, Process'}, or propose it again with a new reason.`}
                  </p>
                  {again ? (
                    <div className="mt-2">
                      <TextInput aria-label="Why it comes late" placeholder="Why it comes late" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
                      <div className="mt-2 flex flex-wrap gap-4">
                        <Button tone="secondary" busy={busy} disabled={!reason.trim()} onClick={() => void run(`/steps/${s.id}/late`, { reason: reason.trim() }).then((ok) => ok && (setAgain(false), setReason('')))}>
                          Propose it again
                        </Button>
                        <Button tone="quiet" onClick={() => setAgain(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="link mt-1 min-h-9" onClick={() => setAgain(true)}>
                      Propose it again
                    </button>
                  )}
                </>
              ) : late ? null : (
                <p className="mt-1">It is not done{readOnly ? '.' : `: mark it done once the rest of ${phase(s.phase)} is, or again with a new reason.`}</p>
              )}
            </div>
          )}
        </>
      ) : null}
      <ErrorLine error={err} />
    </Block>
  );
}

/** Names in a line: "Ann", "Ann and Bo", "Ann, Bo and Cy" (as the API names who signs in as the client). */
const names = (list: readonly string[]): string => (list.length <= 1 ? (list[0] ?? '') : `${list.slice(0, -1).join(', ')} and ${list.at(-1)}`);

/**
 * A deviation answered by NSB for the client (design study 24, L-20 B): only while nobody on the job signs in as its
 * client, once they have told NSB, with how they told NSB; on the record as NSB's, "for the client", on both timelines.
 */
function ForClient({ projectId, deviationId }: { projectId: string; deviationId: string }) {
  const { busy, err, run } = useRun(projectId);
  const [note, setNote] = useState('');
  const answer = (a: 'confirmed' | 'declined') => void run(`/deviations/${deviationId}/answer`, { answer: a, note: note.trim() }).then((ok) => ok && setNote(''));
  return (
    <div className="mt-2">
      <p className="text-sm">Nobody on this job signs in as its client yet: confirm it for them when they tell you. It is on the record as yours, on both timelines.</p>
      <div className="mt-2">
        <TextInput aria-label="How the client told you" placeholder="How the client told you" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
        <Button tone="secondary" busy={busy} disabled={!note.trim()} onClick={() => answer('confirmed')}>
          Confirm it for the client
        </Button>
        <button type="button" className="link min-h-11 text-sm disabled:opacity-40" disabled={busy || !note.trim()} onClick={() => answer('declined')}>
          They declined it
        </button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

/** Passing a gate, on an override with a reason when its work is not done; an open exception, cleared with a note. */
function GateControls({ projectId, model, step: s, inList = false }: { projectId: string; model: ProcessModel; step: ProcessStepDto; inList?: boolean }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const { busy, err, run } = useRun(projectId);
  const gate = s.gate!;
  const live = model.dto.steps.filter((x) => !x.off);
  const direct = model.dto.links
    .filter((l) => l.stepId === s.id)
    .map((l) => live.find((x) => x.id === l.needsId))
    .filter((x): x is ProcessStepDto => Boolean(x && x.state !== 'done'));
  // The gate that closes a phase waits on the rest of its phase too, by rule (core `phaseHolds`): marked done before it
  // is, it is a deviation the client confirms (design study 21); on NSB's own job the reason is enough.
  const phase = (model.holds.get(s.id) ?? []).map((id) => live.find((x) => x.id === id)).filter((x): x is ProcessStepDto => Boolean(x && x.state !== 'done'));
  const notDone = [...new Set([...direct, ...phase])];
  const phaseName = phasesOf(model.dto.tree).find((p) => p.key === s.phase)?.name ?? 'its phase';
  // Marked done early and waiting on the client while the rest of its phase is open: nothing to mark again yet (the API
  // refuses a second proposal). Once the rest is done it is marked done as any gate is.
  const waiting = s.deviation?.kind === 'early_gate' && s.deviation.answer === 'waiting' && phase.length > 0;
  // Held by a gate or inspection before it not passed (L-17 B), or with a task of its own not done (L-23): the way past
  // is that one first, or its tasks.
  const held = Boolean(heldWords(model, s));
  const tasks = tasksWords(model, s);
  const readOnly = useReadOnly();
  if (s.state === 'done') {
    const end = gate.overrideReason ? ', on an override.' : '.';
    return (
      <Frame label="Done" inList={inList}>
        {/* A block, not a paragraph: the day's pencil opens a field with its refusal under it. */}
        <div className="text-sm">
          {/* Approved by the client outside the portal, recorded by NSB (L-16 A): who recorded it, and its evidence, on the
              day of its record. Marked done by NSB, its day with the pencil to set it after the fact (P-32); approved by the
              client in the portal, theirs, with none. */}
          {gate.passed?.recorded && gate.record ? (
            `${recordedWords(gate.record)}${end}`
          ) : (
            <DayLine projectId={projectId} step={s} field="doneOn" words="Done" line={`${gateDone(gate.passed, s.doneOn, { long: true, marked: true })}${end}`} />
          )}
        </div>
        {gate.passed?.recorded && gate.record ? <RecordedApproval projectId={projectId} record={gate.record} /> : null}
        {gate.overrideReason ? (
          <div className="mt-2">
            <p className="text-sm">
              <span className="text-alert">{gate.clearedAt ? 'Exception cleared' : 'Open exception'}:</span> {gate.overrideReason}
            </p>
            {gate.clearedAt ? (
              <p className="meta mt-1">Cleared: {gate.clearedNote}</p>
            ) : readOnly ? null : (
              <div className="mt-2">
                <TextInput aria-label="How it was settled" value={note} onChange={(e) => setNote(e.target.value)} placeholder="How it was settled" />
                <div className="mt-2">
                  <Button tone="secondary" busy={busy} disabled={!note.trim()} onClick={() => void run(`/steps/${s.id}/clear`, { note: note.trim() })}>
                    Clear the exception
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : null}
        <ErrorLine error={err} />
        {inList ? null : <KeptLine model={model} />}
      </Frame>
    );
  }
  // A gate the client approves, on a client's job, with nothing before it open: their approval given outside the portal
  // is recorded here, with its day and its evidence (L-16 A), as their own Approve would pass it.
  const recordable = s.owner === 'client' && model.dto.kind !== 'internal' && notDone.length === 0;
  return (
    <Frame label="Not done yet" inList={inList}>
      {gate.clientNote ? <p className="mb-2 text-sm text-ink-2">The client is told: “{gate.clientNote}”</p> : null}
      {readOnly ? null : <Held model={model} step={s} inList />}
      {tasks && !readOnly ? <p className="mb-2 text-sm text-alert">{tasks}</p> : null}
      {/* Marked done early, it waits on the client to confirm it, not done (the deviation above): nothing to mark yet. */}
      {waiting || held || tasks || readOnly ? null : recording ? (
        <RecordApproval projectId={projectId} stepId={s.id} onDone={() => setRecording(false)} onCancel={() => setRecording(false)} />
      ) : !open ? (
        <div className="flex flex-wrap gap-x-3 gap-y-2">
          <Button tone="secondary" onClick={() => setOpen(true)}>
            Mark it done
          </Button>
          {recordable ? (
            <Button tone="secondary" onClick={() => setRecording(true)}>
              Record the client’s approval
            </Button>
          ) : null}
        </div>
      ) : (
        <div>
          {notDone.length ? (
            <p className="mb-2 text-sm text-alert">
              {notDone.slice(0, 3).map((x) => titleCase(x.title)).join(', ')}
              {notDone.length > 3 ? ` and ${notDone.length - 3} more` : ''} {notDone.length === 1 ? 'is' : 'are'} not done.{' '}
              {phase.length
                ? model.dto.kind === 'internal'
                  ? `Marking it done before the rest of ${phaseName} needs a reason: on NSB’s own job the reason is enough, and it stays an open exception until the rest is done.`
                  : `Marking it done before the rest of ${phaseName} needs a reason: the client reads it and confirms it, and until they do it is not done.`
                : 'Marking it done now is an override: it needs a reason, and stays an open exception until cleared.'}
            </p>
          ) : (
            <p className="mb-2 text-sm">Once done, it stays done.</p>
          )}
          <TextInput aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={notDone.length ? 'Why it is done now' : 'A note (optional)'} />
          <div className="mt-2 flex gap-4">
            <Button busy={busy} disabled={notDone.length > 0 && !reason.trim()} onClick={() => void run(`/steps/${s.id}/pass`, { reason: reason.trim() || null }).then((ok) => ok && setOpen(false))}>
              {notDone.length ? 'Mark it done on an override' : 'Mark it done'}
            </Button>
            <Button tone="quiet" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <ErrorLine error={err} />
      {inList ? null : <Settle projectId={projectId} step={s} />}
      {inList ? null : <KeptLine model={model} />}
    </Frame>
  );
}

/**
 * The authority's hold: an inspection is passed, passed on an override (the
 * work before it not done, with a reason that stays an open exception), or
 * recorded as not passed, with what the inspector said.
 */
function InspectionControls({ projectId, model, step: s, inList = false }: { projectId: string; model: ProcessModel; step: ProcessStepDto; inList?: boolean }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [mode, setMode] = useState<'closed' | 'pass' | 'fail'>('closed');
  const { busy, err, run } = useRun(projectId);
  const insp = s.inspection!;
  const live = model.dto.steps.filter((x) => !x.off);
  // What it waits on not done: its waits, and in a step's list what is listed above it (L-25d: the list's order is the order).
  const notDone = [
    ...new Set([
      ...model.dto.links
        .filter((l) => l.kind === 'requires' && l.stepId === s.id)
        .map((l) => live.find((x) => x.id === l.needsId))
        .filter((x): x is ProcessStepDto => Boolean(x && x.state !== 'done')),
      ...listedAboveOpen(model, s),
    ]),
  ];
  // Held by a gate or inspection before it not passed (L-17 B): it is neither passed nor recorded as not passed until that is.
  const held = Boolean(heldWords(model, s));
  const readOnly = useReadOnly();
  if (s.state === 'done') {
    return (
      <Frame label="Passed" inList={inList}>
        <div className="text-sm">
          {/* The day it passed, with the pencil to set it after the fact, as a task's (P-32). */}
          <DayLine projectId={projectId} step={s} field="doneOn" words="Passed" line={`Passed${s.doneOn ? ` ${long(s.doneOn)}` : ''}${insp.overrideReason ? ' on an override.' : '.'}`} />
        </div>
        {insp.overrideReason ? (
          <div className="mt-2">
            <p className="text-sm">
              <span className="text-alert">{insp.clearedAt ? 'Exception cleared' : 'Open exception'}:</span> {insp.overrideReason}
            </p>
            {insp.clearedAt ? (
              <p className="meta mt-1">Cleared: {insp.clearedNote}</p>
            ) : readOnly ? null : (
              <div className="mt-2">
                <TextInput aria-label="How it was settled" value={note} onChange={(e) => setNote(e.target.value)} placeholder="How it was settled" />
                <div className="mt-2">
                  <Button tone="secondary" busy={busy} disabled={!note.trim()} onClick={() => void run(`/steps/${s.id}/clear`, { note: note.trim() })}>
                    Clear the exception
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : null}
        <ErrorLine error={err} />
        {inList ? null : <KeptLine model={model} />}
      </Frame>
    );
  }
  return (
    <Frame label={s.state === 'in_progress' ? 'Did not pass' : 'Not yet'} inList={inList}>
      <p className="mb-2 text-sm text-ink-2">The inspector’s hold: the work after it waits until it passes.{s.state === 'in_progress' ? ' It did not pass last time; the inspector’s notes are on the record.' : ''}</p>
      {readOnly ? null : <Held model={model} step={s} inList />}
      {held || readOnly ? null : mode === 'closed' ? (
        <div className="flex flex-wrap gap-4">
          <Button tone="secondary" onClick={() => setMode('pass')}>
            Passed
          </Button>
          <Button tone="quiet" onClick={() => setMode('fail')}>
            Did not pass
          </Button>
        </div>
      ) : mode === 'pass' ? (
        <div>
          {notDone.length ? (
            <p className="mb-2 text-sm text-alert">
              {notDone.slice(0, 3).map((x) => titleCase(x.title)).join(', ')}
              {notDone.length > 3 ? ` and ${notDone.length - 3} more` : ''} {notDone.length === 1 ? 'is' : 'are'} not done. Recording a pass now is an override: it needs a reason, and stays an open exception until cleared.
            </p>
          ) : null}
          <TextInput aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={notDone.length ? 'Why record it as passed now' : 'A note (optional)'} />
          <div className="mt-2 flex gap-4">
            <Button busy={busy} disabled={notDone.length > 0 && !reason.trim()} onClick={() => void run(`/steps/${s.id}/pass`, { reason: reason.trim() || null }).then((ok) => ok && setMode('closed'))}>
              {notDone.length ? 'Record it on an override' : 'Record it as passed'}
            </Button>
            <Button tone="quiet" onClick={() => setMode('closed')}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <TextInput aria-label="The inspector’s notes" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What the inspector wants changed" />
          <div className="mt-2 flex gap-4">
            <Button tone="danger" busy={busy} disabled={!note.trim()} onClick={() => void run(`/steps/${s.id}/fail`, { note: note.trim() }).then((ok) => ok && (setNote(''), setMode('closed')))}>
              Record that it did not pass
            </Button>
            <Button tone="quiet" onClick={() => setMode('closed')}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <ErrorLine error={err} />
      {inList ? null : <Settle projectId={projectId} step={s} />}
      {inList ? null : <KeptLine model={model} />}
    </Frame>
  );
}

/**
 * The package: the trade this step is a share of, who does it (NSB unless a
 * sub, supplier or consultant is named), what the bid is for, and the quote
 * it is priced from (its amount feeds no total while the estimate is out).
 */
function Package({ projectId, step: s, card = false }: { projectId: string; step: ProcessStepDto; card?: boolean }) {
  const { data } = useTrades();
  const readOnly = useReadOnly();
  const { err, run } = useRun(projectId);
  const [scope, setScope] = useState(s.scopeNote ?? '');
  const trades = (data?.trades ?? []).filter((t) => !t.archived || t.key === s.trade);
  const performers = (data?.performers ?? []).filter((p) => !p.archived || p.id === s.performer?.id);
  const forTrade = s.trade ? performers.filter((p) => p.kind === 'nsb' || p.trades.includes(s.trade!)) : performers;
  const others = performers.filter((p) => !forTrade.includes(p));
  const tradeName = (k: string | null) => trades.find((t) => t.key === k)?.name ?? k ?? '';
  // An archived job's package reads as it ended (L-19 A): its trade, who did it and what it included, in words.
  if (readOnly) {
    if (!s.trade) return null;
    return (
      <Block label="The package">
        <p className="text-sm">
          {tradeName(s.trade)}
          {s.performer ? ` · ${s.performer.name}` : ' · nobody named'}
        </p>
        {s.scopeNote ? <p className="mt-1 whitespace-pre-line text-sm text-ink-2">{s.scopeNote}</p> : null}
      </Block>
    );
  }
  // Its trade is on its card on the setup page (design study 25): the package's own record stays here.
  if (card && !s.trade) return null;
  return (
    <Block label={s.trade ? 'The package' : 'Trade'}>
      <div className="grid gap-2 sm:grid-cols-2">
        {card ? null : (
          <label className="block">
            <span className="label block">Trade</span>
            <select className="field" value={s.trade ?? ''} onChange={(e) => void run(`/steps/${s.id}`, { tradeKey: e.target.value || null }, 'PATCH')}>
              <option value="">Not a package</option>
              <TradeOptions trades={trades} current={s.trade} />
            </select>
          </label>
        )}
        {s.trade ? (
          <label className="block">
            <span className="label block">Who does it</span>
            <select className="field" value={s.performer?.id ?? ''} onChange={(e) => void run(`/steps/${s.id}`, { performerId: e.target.value || null }, 'PATCH')}>
              <option value="">Nobody named yet</option>
              {forTrade.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.kind !== 'nsb' ? ` · ${PERFORMER_KIND_WORDS[p.kind].toLowerCase()}` : ''}
                </option>
              ))}
              {others.length ? (
                <optgroup label={`Not listed under ${tradeName(s.trade)}`}>
                  {others.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {PERFORMER_KIND_WORDS[p.kind].toLowerCase()}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </label>
        ) : null}
      </div>
      {s.trade ? (
        <>
          <p className="meta mt-1.5">
            One performer’s share of {tradeName(s.trade).toLowerCase()}: what it includes and leaves out, and its lines; its quotes are in its log. Subs and suppliers are added in{' '}
            <Link to="/settings/trades" className="link">
              Settings → Trades and subs
            </Link>
            .
          </p>
          <div className="label mb-1 mt-3">What it includes and leaves out</div>
          <textarea
            className="field resize-y text-sm"
            aria-label="What it includes and leaves out"
            rows={3}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            onBlur={() => scope.trim() !== (s.scopeNote ?? '') && void run(`/steps/${s.id}`, { scopeNote: scope.trim() || null }, 'PATCH')}
            placeholder="What is in, what is out; durations, crew, lead times, alternates. What a bid is for."
          />
        </>
      ) : (
        <p className="meta mt-1.5">Give it a trade and it becomes a package: one performer’s share of that trade, with what it includes and leaves out.</p>
      )}
      <ErrorLine error={err} />
    </Block>
  );
}

/**
 * The estimate a step delivers (design study 17; Jason, 2026-10-07: "a simple band, no calculator, just a high low
 * with a note"): a low and a high in whole dollars, one number both the same, and a note the client reads. Saved, it
 * is the funnel's band from today, on the record; a new one later is a new band, the old kept as it was. The step is
 * done once one is set.
 */
function Estimate({ projectId, step: s, inList = false, onSaved }: { projectId: string; step: ProcessStepDto; inList?: boolean; onSaved?: () => void }) {
  const { busy, err, run } = useRun(projectId);
  const cur = s.estimate.current;
  const [low, setLow] = useState<number | null>(cur?.lowCents ?? null);
  const [high, setHigh] = useState<number | null>(cur?.highCents ?? null);
  const [note, setNote] = useState(cur?.note ?? '');
  const money = (a: number, b: number) => (a === b ? formatMoney(a) : `${formatMoney(a)} to ${formatMoney(b)}`);
  const changed = low !== (cur?.lowCents ?? null) || high !== (cur?.highCents ?? null) || note.trim() !== (cur?.note ?? '');
  const save = () => {
    if (low === null || high === null) return;
    void run(`/steps/${s.id}/estimate`, { lowCents: low, highCents: high, note: note.trim() }).then((ok) => ok && onSaved?.());
  };
  return (
    <Frame label="Estimate" inList={inList}>
      {inList ? null : cur ? (
        <p className="text-sm">
          <span className="num">{money(cur.lowCents, cur.highCents)}</span>, set {long(cur.on)}.{cur.note ? ` ${cur.note}` : ''}
        </p>
      ) : (
        <p className="text-sm">Not set yet.{s.state !== 'done' ? ' This step is done once it is.' : ''}</p>
      )}
      {inList && !cur && s.state !== 'done' ? <p className="text-sm">The step is done once it is set.</p> : null}
      <div className="mt-2 grid items-center gap-2 sm:grid-cols-[minmax(0,9rem)_auto_minmax(0,9rem)]">
        <MoneyInput aria-label="Low, dollars" value={low} onChange={setLow} />
        <span className="num text-sm text-ink-3">to</span>
        <MoneyInput aria-label="High, dollars" value={high} onChange={setHigh} />
      </div>
      <div className="label mb-1 mt-3">Note</div>
      <TextInput aria-label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="How it was reached; the client reads this" />
      <div className="mt-2">
        <Button onClick={save} busy={busy} disabled={low === null || high === null || high < low || !changed}>
          {cur ? 'Save a new estimate' : 'Save the estimate'}
        </Button>
      </div>
      <p className="meta mt-1.5">A low and a high in whole dollars; one number is both the same. Saved, it is the funnel’s band from today, on the record; a new one later is a new band, the old kept as it was.</p>
      <ErrorLine error={err} />
    </Frame>
  );
}

/**
 * Why a step was not marked done, and where the hold on it is: a gate or inspection before it however far back (the
 * API's words name it and the way past it, L-17 B), its tasks (L-23), its estimate, or a decision's choice.
 */
function Refused({ err, code }: { err: string | null; code: string | null }) {
  if (code === 'gate_closed' || code === 'inspection_open' || code === 'tasks') return <p className="mt-2 text-sm text-alert">{err}</p>;
  if (code === 'estimate') return <p className="mt-2 text-sm text-alert">{err} Set it below first.</p>;
  if (code === 'choice') return <p className="mt-2 text-sm text-alert">{err} Choose from them first.</p>;
  return <ErrorLine error={err} />;
}

/**
 * A decision's options on its panel (design study 22; Jason, 2026-10-07: "either select one of many or many of many"):
 * not decided, what it offers to choose from, one or any, the choice going with Done; decided, what was chosen, by whom
 * and when (the latest choice: decided again, it is the new one).
 */
function Decided({
  decision: d,
  done,
  picked,
  busy,
  onPick,
}: {
  decision: NonNullable<ProcessStepDto['decision']>;
  done: boolean;
  picked: string[];
  busy: boolean;
  onPick: (ids: string[]) => void;
}) {
  const readOnly = useReadOnly();
  if (done) {
    return d.chosen ? (
      <div className="mb-2">
        <p className="text-sm">Chosen: {chosenWords(d.chosen.options)}</p>
        <p className="meta">
          {d.chosen.by ?? 'Someone'}
          {d.chosen.role === 'client' ? ', the client' : ''} · {dateWords(d.chosen.at, 'date')}
        </p>
      </div>
    ) : (
      <p className="meta mb-2">Done with nothing chosen: it was marked done another way.</p>
    );
  }
  // Opened again after the master took its options away: nothing to choose from, and it is decided as any step is. An
  // archived job's decision is kept as it ended (L-19 A): nothing to choose.
  if (!d.options.length || readOnly) return null;
  return (
    <div className="mt-1">
      <DecisionChoice legend={`${chooseWords(d.choose)}, then Done`} options={d.options} choose={d.choose} value={picked} onChange={onPick} disabled={busy} />
    </div>
  );
}

/**
 * A step's own tasks, each with its own controls (B3; Jason: "List the tasks in the opened step"): a piece of work
 * marked done or not, an inspection passed or not passed by the inspector (on an override, with a reason, while the
 * work before it is not done), a gate marked done. The holds a step's list carries (Site checked ready first, the
 * Inspection last) are worked here; the step stands where they do. By the phase each is done in (design study 31,
 * slice 4), as on the master's card: those done early first, its own phase's last, its special tasks with them; setting
 * the job up (`edits`, on a step that holds a list alone), a line in each group to type a task into, an earlier phase one
 * click away and each task's Done in (or a drag into another group), each a change of the job's shape held in its draft
 * (`processAction`). With no task and nothing to add, no block at all.
 */
function Tasks({ projectId, model, holder, edits }: { projectId: string; model: ProcessModel; holder: ProcessStepDto; edits: boolean }) {
  const { busy, err, code, run } = useRun(projectId);
  const live = model.dto.steps.filter((x) => !x.off);
  const rows: ProcessStepDto[] = [];
  const walk = (id: string) => {
    for (const t of live.filter((x) => x.parentId === id)) {
      rows.push(t);
      walk(t.id);
    }
  };
  walk(holder.id);
  // The special tasks the tree switches on for it (Jason, 2026-10-07: "an estimate, design review pre inspection, permit
  // or inspection when they are on all appear as special tasks in the nodes check list"): the inspections are rows of
  // its list already; the estimate, the design review and the permit are read from what they are.
  const node = holder.key ? model.tree.node(holder.key) : undefined;
  const permit = node?.permit ? (live.find((x) => x.key === PERMIT_KEY && x.instance === null) ?? null) : undefined;
  // Its pattern language (spec "Pattern Mapping" §4.5): done once it is locked, on Patterns.
  const language = (holder.specials ?? []).find((x) => x.key === 'patterns');
  // Its design review is in once everyone asked has finished it, whether or not the step is done yet (L-23: a task of
  // its own still open keeps it under way).
  const reviewed = useReviewed(projectId, holder);
  // Its review in, it is done once its other tasks are and nothing before it holds it (L-17 B): the nearest hold named.
  const holdOn = holder.state !== 'done' ? (model.held.get(holder.id)?.[0] ?? null) : null;
  const reviewedWords = holdOn
    ? `Reviewed: done once ${stepCalled(model.process, holdOn.id)} is ${model.step(holdOn.id)?.kind === 'inspection' ? 'passed' : 'done'}`
    : 'Reviewed: its other tasks still to do';
  const special: { key: string; done: number; total: number; row: ReactNode }[] = [
    ...(holder.estimate.delivers ? [{ key: 'estimate', done: holder.estimate.history.length, total: Math.max(1, holder.estimate.history.length), row: <EstimateTask key="estimate" projectId={projectId} step={holder} /> }] : []),
    ...(holder.review ? [{ key: 'review', done: reviewed ? 1 : 0, total: 1, row: <ReviewTask key="review" projectId={projectId} step={holder} reviewed={reviewed} reviewedWords={reviewedWords} /> }] : []),
    ...(permit !== undefined ? [{ key: 'permit', done: permit?.state === 'done' ? 1 : 0, total: 1, row: <PermitTask key="permit" projectId={projectId} permit={permit} /> }] : []),
    ...(language ? [{ key: 'patterns', done: language.done ? 1 : 0, total: 1, row: <LanguageTask key="patterns" projectId={projectId} locked={language.done} /> }] : []),
  ];
  if (!rows.length && !special.length && !edits) return null;
  // Only what is open to do, and what is done: on a step done, a special task not done is nothing left to do (`tasksCount`).
  const count = tasksCount(rows.map((r) => ({ done: r.state === 'done' })), special, holder.state === 'done');
  // A task under another one in the same group sits under it; one in another group than the task holding it, at the left.
  const depthIn = (t: ProcessStepDto, g: TaskGroup<PhaseKey, ProcessStepDto>) => {
    let d = 0;
    for (let up = t.parentId; up && up !== holder.id && g.tasks.some((x) => x.id === up); up = model.step(up)?.parentId ?? null) d++;
    return d;
  };
  return (
    <Block label={count.of ? `Tasks · ${count.done} of ${count.of} done` : 'Tasks'}>
      <TaskGroups<PhaseKey, ProcessStepDto>
        phases={phasesOf(model.dto.tree)}
        own={model.phases.get(holder.id) ?? null}
        tasks={rows}
        phaseOf={(t) => model.phases.get(t.id) ?? null}
        idOf={(t) => t.id}
        nameOf={(t) => titleCase(t.title)}
        listClass="divide-y divide-rule"
        row={(t, g, drag, doneIn) => <TaskRow key={t.id} projectId={projectId} model={model} step={t} depth={depthIn(t, g)} holds={rows.some((r) => r.parentId === t.id)} drag={drag} doneIn={doneIn} />}
        ownAfter={special.length ? special.map((x) => x.row) : undefined}
        edits={
          edits
            ? {
                busy,
                // Its own phase's: in its step's, as the job reads it; an earlier one's, done early there.
                add: (title, phase, own) => run('/steps', { title, kind: 'work', parentId: holder.id, ...(own || phase === null ? {} : { phase }) }),
                move: (id, phase) => void run(`/steps/${id}`, { phase }, 'PATCH'),
              }
            : null
        }
      />
      <Refused err={err} code={code} />
    </Block>
  );
}

/** A special task's row: its name, where it stands under it, and what it opens. */
function SpecialRow({ name, standing, done, children, open }: { name: string; standing: ReactNode; done: boolean; children?: ReactNode; open?: ReactNode }) {
  return (
    <li className="py-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-sm">{name}</span>
          <span className={`meta ${done ? '!text-moss' : ''}`}>{standing}</span>
        </span>
        {children}
      </div>
      {open}
    </li>
  );
}

/**
 * The rough ballpark estimate, a special task (Jason, 2026-10-07: "a special task that exposes a high low range with date
 * on save that is editable by admins"): done once a low and a high are set, the day it was set beside it (an admin's to
 * change, the funnel's band starting there), and the range set or set again here.
 */
function EstimateTask({ projectId, step: s }: { projectId: string; step: ProcessStepDto }) {
  const history = s.estimate.history;
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(history.length === 0 && !readOnly);
  const money = (a: number, b: number) => (a === b ? formatMoney(a) : `${formatMoney(a)} to ${formatMoney(b)}`);
  const form = open ? <Estimate projectId={projectId} step={s} inList onSaved={() => setOpen(false)} /> : null;
  // Each range set is its own row, the first first, a new one added below the last (Jason, 2026-10-07: "setting a new
  // range should add the rough ballpark estimate below the last one"); none is ever overwritten.
  return (
    <>
      {history.map((e) => (
        <SpecialRow
          key={e.id}
          name="Rough Ballpark Estimate"
          done
          standing={
            <>
              <span className="num">{money(e.lowCents, e.highCents)}</span> · <EstimateDay projectId={projectId} step={s} estimate={e} />
            </>
          }
        />
      ))}
      {readOnly ? (
        history.length ? null : <SpecialRow name="Rough Ballpark Estimate" done={false} standing="Not set" />
      ) : history.length ? (
        <li className="py-1.5">
          <button type="button" className="link min-h-9 text-sm" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? 'Close' : 'Set a new range'}
          </button>
          {form}
        </li>
      ) : (
        <SpecialRow name="Rough Ballpark Estimate" done={false} standing="Not set yet" open={form}>
          <button type="button" className="link min-h-9 shrink-0 text-sm" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? 'Close' : 'Set the range'}
          </button>
        </SpecialRow>
      )}
    </>
  );
}

/** The day one of a step's ranges counts from, with the pencil to change it (an admin's; never a day to come). */
function EstimateDay({ projectId, step: s, estimate: cur }: { projectId: string; step: ProcessStepDto; estimate: ProcessStepDto['estimate']['history'][number] }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(cur.on);
  const { busy, err, run } = useRun(projectId);
  const readOnlyDays = useReadOnly();
  const me = useMe().data;
  const admin = !readOnlyDays && isAdmin(me);
  const today = easternToday();
  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        set {long(cur.on)}
        {admin ? (
          <EditMark
            label="Change the day the estimate was set"
            onClick={() => {
              setValue(cur.on);
              setEditing(true);
            }}
          />
        ) : null}
      </span>
    );
  }
  return (
    <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
      <label className="inline-flex items-center gap-2">
        <span>The day it was set</span>
        <input type="date" className="field w-auto py-1" value={value} max={today} onChange={(e) => setValue(e.target.value)} />
      </label>
      <Button tone="quiet" busy={busy} disabled={!value || value > today} onClick={() => void run(`/steps/${s.id}/estimate/day`, { on: value, estimateId: cur.id }).then((ok) => ok && setEditing(false))}>
        Save
      </Button>
      <Button tone="quiet" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      <ErrorLine error={err} />
    </span>
  );
}

/**
 * Whether a step's design review is in (everyone asked has finished it), read from the iterations as Design reviews
 * reads them: done with the step, else as its iteration stands.
 */
function useReviewed(projectId: string, s: ProcessStepDto): boolean {
  const { data } = useApi<ReviewSetDto[]>(['project', projectId, 'reviews'], s.review && s.state !== 'done' ? `/projects/${projectId}/reviews` : null);
  return s.state === 'done' || Boolean(data?.some((set) => set.task?.id === s.id && set.reviewed));
}

/** Its design review, a special task: done when everyone asked has reviewed it, never by hand (B1); the day it was, an admin's to change. */
function ReviewTask({ projectId, step: s, reviewed, reviewedWords }: { projectId: string; step: ProcessStepDto; reviewed: boolean; reviewedWords: string }) {
  return (
    <SpecialRow
      name="Design Review"
      done={reviewed}
      standing={s.state === 'done' && s.doneOn ? <DayLine projectId={projectId} step={s} field="doneOn" words="Reviewed" /> : reviewed ? reviewedWords : 'Waiting on its review'}
    >
      <Link to={`/p/${projectId}/reviews`} className="link min-h-9 shrink-0 text-sm">
        Design reviews
      </Link>
    </SpecialRow>
  );
}

/** Its pattern language, a special task: done once it is locked, on Patterns, the only way its step is done. */
function LanguageTask({ projectId, locked }: { projectId: string; locked: boolean }) {
  return (
    <SpecialRow name="Pattern Language" done={locked} standing={locked ? 'Locked' : 'Not locked yet'}>
      <Link to={`/p/${projectId}/patterns`} className="link min-h-9 shrink-0 text-sm">
        Patterns
      </Link>
    </SpecialRow>
  );
}

/**
 * Its permit, a special task: done when Permits in hand is, on this job (the step comes after it); the day it was, an
 * admin's to change, a gate's too unless the client approved it (P-32).
 */
function PermitTask({ projectId, permit }: { projectId: string; permit: ProcessStepDto | null }) {
  const standing = !permit
    ? 'Permits in hand is not on this job yet'
    : permit.state === 'done' && permit.doneOn
      ? <DayLine projectId={projectId} step={permit} field="doneOn" words="In hand" />
      : 'Not in hand yet';
  return <SpecialRow name="Permit" done={permit?.state === 'done'} standing={standing} />;
}

function TaskRow({
  projectId,
  model,
  step: t,
  depth,
  holds,
  drag,
  doneIn,
}: {
  projectId: string;
  model: ProcessModel;
  step: ProcessStepDto;
  depth: number;
  holds: boolean;
  drag?: TaskDrag | null;
  /** Setting the job up, the phase it is done in, to change (`TaskGroups`' Done in). */
  doneIn?: ReactNode;
}) {
  const { busy, err, code, run } = useRun(projectId);
  const [open, setOpen] = useState(false);
  const word = t.state === 'done' ? (t.kind === 'inspection' ? 'Passed' : t.kind === 'milestone' ? 'Reached' : 'Done') : t.kind === 'inspection' && t.state === 'in_progress' ? 'Did not pass' : t.state === 'in_progress' ? 'Under way' : 'Not done yet';
  const when = t.state === 'done' && t.doneOn ? ` · ${long(t.doneOn)}` : '';
  // A gate in the list says who approved it, or that it is done (study 09, gates A), as its tile does.
  // A gate approved by the client outside the portal reads as theirs, recorded by NSB (L-16 A).
  const standing =
    t.kind === 'gate' && t.state === 'done'
      ? t.gate?.passed?.recorded && t.gate.record
        ? recordedWords(t.gate.record, { by: false })
        : gateDone(t.gate?.passed, t.doneOn, { long: true })
      : `${word}${when}`;
  const exception = (t.inspection?.overrideReason && !t.inspection.clearedAt) || (t.gate?.overrideReason && !t.gate.clearedAt);
  // A decision in the list with options is decided by choosing (design study 22): it opens its options.
  const choosing = t.kind === 'decision' && Boolean(t.decision?.options.length) && t.state !== 'done';
  // The client's own task stays theirs (L-23): on their Waiting on you, NSB marking it for them on Decisions.
  const theirs = isClientTask(t);
  // Held by an inspection listed above it, or a gate or inspection further back (L-17 B, L-25d): not marked done yet.
  const hold = t.state !== 'done' && !holds ? (model.held.get(t.id)?.[0] ?? null) : null;
  const holdTitle = hold ? stepCalled(model.process, hold.id, undefined, t.id) : undefined;
  // An archived job's tasks read as they ended (L-19 A): nothing to press. On a completed job a task done stays done (L-30).
  const readOnly = useReadOnly();
  const stays = useContext(Kept) === 'shape' && t.state === 'done';
  const toggle = taskToggle(t.state === 'done');
  // A task holding tasks of its own stands where they do; an inspection or a gate opens its own controls.
  const control =
    holds || t.review || readOnly ? null : theirs ? (
      t.state === 'done' ? null : (
        <Link to={`/p/${projectId}/decisions`} className="link min-h-9 shrink-0 text-sm">
          Decisions
        </Link>
      )
    ) : hold && t.kind !== 'inspection' && t.kind !== 'gate' ? null : t.kind === 'inspection' || t.kind === 'gate' || choosing ? (
      <button type="button" className="link min-h-9 shrink-0 text-sm" aria-expanded={open} onClick={() => setOpen(!open)}>
        {/* Held, it is neither recorded nor marked yet (L-17 B): opened, it says what holds it. */}
        {open ? 'Close' : t.state === 'done' ? (exception ? 'The exception' : 'Details') : hold ? 'Details' : t.kind === 'inspection' ? 'Record it' : choosing ? 'Decide it' : 'Mark it done'}
      </button>
    ) : (
      // Done, a quiet grey Reopen; not done, Mark it done (P-31, #113: "a greyed out button that is like "reopen"").
      <button
        type="button"
        disabled={busy || stays}
        title={stays ? keptWords('shape', model.dto.tree).undone : undefined}
        onClick={() => void run(`/steps/${t.id}`, { state: toggle.to }, 'PATCH')}
        className={toggle.look}
      >
        {toggle.words}
      </button>
    );
  return (
    <li {...drag} className={`py-1.5 ${drag ? 'cursor-grab active:cursor-grabbing' : ''}`} style={{ paddingLeft: depth * 16 }}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="min-w-0">
          <span className="block text-sm">{titleCase(t.title)}</span>
          <span className={`meta ${t.state === 'done' ? '!text-moss' : t.kind === 'inspection' && t.state === 'in_progress' ? '!text-alert' : ''}`}>
            {t.kind === 'inspection' ? (t.owner === 'nsb' ? 'Pre-install inspection · ' : 'Inspection · ') : ''}
            {/* Done or passed, its day beside it, an admin's to change (Jason, 2026-10-07); a gate's in its own words, its
                pencil there once NSB marked it done, never when the client approved it (P-32). */}
            {t.state === 'done' && t.doneOn && !holds ? (
              <DayLine projectId={projectId} step={t} field="doneOn" words={theirs ? `${word}, the client’s` : word} line={t.kind === 'gate' ? standing : undefined} />
            ) : theirs ? (
              clientTaskStanding(model, t)
            ) : hold && holdTitle && !readOnly ? (
              `Held · ${holdTitle} is not ${model.step(hold.id)?.kind === 'inspection' ? 'passed' : 'done'} yet`
            ) : (
              standing
            )}
            {exception ? ', on an override' : ''}
          </span>
          <TaskLogCount taskId={t.id} />
        </span>
        {doneIn || control ? (
          <span className="flex shrink-0 items-center gap-3">
            {doneIn}
            {control}
          </span>
        ) : null}
      </div>
      {open && t.kind === 'inspection' ? <InspectionControls projectId={projectId} model={model} step={t} inList /> : null}
      {open && t.kind === 'gate' ? <GateControls projectId={projectId} model={model} step={t} inList /> : null}
      {open && choosing ? <DecideInList projectId={projectId} step={t} onDone={() => setOpen(false)} /> : null}
      {/* Decided, what was chosen, as its own panel says it. */}
      {t.kind === 'decision' && t.state === 'done' && t.decision?.chosen ? <p className="meta">Chosen: {chosenWords(t.decision.chosen.options)}</p> : null}
      <Refused err={err} code={code} />
    </li>
  );
}

/** A decision in a step's list, decided by choosing from its options (design study 22), as its own panel decides it. */
function DecideInList({ projectId, step: t, onDone }: { projectId: string; step: ProcessStepDto; onDone: () => void }) {
  const { busy, err, code, run } = useRun(projectId);
  const [picked, setPicked] = useState<string[]>([]);
  const d = t.decision!;
  const ready = !('refused' in decisionChoice({ title: t.title, options: d.options, choose: d.choose }, picked));
  return (
    <Frame label="Decide it" inList>
      <DecisionChoice legend={chooseWords(d.choose)} options={d.options} choose={d.choose} value={picked} onChange={setPicked} disabled={busy} />
      <div className="flex gap-4">
        <Button busy={busy} disabled={!ready} onClick={() => void run(`/steps/${t.id}`, { state: 'done', choice: picked }, 'PATCH').then((ok) => ok && onDone())}>
          Mark it decided
        </Button>
        <Button tone="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
      <Refused err={err} code={code} />
    </Frame>
  );
}

/** "Done up to here": the step and everything before it done as of a day, for a job loaded as it stands. */
function Settle({ projectId, step: s }: { projectId: string; step: ProcessStepDto }) {
  const [open, setOpen] = useState(false);
  const [on, setOn] = useState(easternToday);
  const { busy, err, run } = useRun(projectId);
  if (useReadOnly()) return null;
  if (!open) {
    return (
      <button type="button" className="link mt-2 block min-h-9 text-sm text-ink-3 hover:text-ink" onClick={() => setOpen(true)}>
        Done up to here…
      </button>
    );
  }
  return (
    <div className="mt-3 rounded-[2px] bg-sheet p-3">
      <p className="text-sm">Mark this and everything before it done, as of a day: for a job loaded as it stands.</p>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        <input type="date" className="field max-w-44" aria-label="Done on" value={on} onChange={(e) => setOn(e.target.value)} />
        <Button tone="secondary" busy={busy} onClick={() => void run(`/steps/${s.id}/settle`, { on }).then((ok) => ok && setOpen(false))}>
          Mark them done
        </Button>
        <Button tone="quiet" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

function Days({ projectId, step: s }: { projectId: string; step: ProcessStepDto }) {
  const [days, setDays] = useState(s.days === null ? '' : String(s.days));
  const { err, run } = useRun(projectId);
  if (useReadOnly()) return s.days === null ? null : <p className="mt-2 text-sm">Takes {s.days} {s.days === 1 ? 'day' : 'days'}</p>;
  const save = () => {
    const n = days.trim() === '' ? null : Math.max(0, Math.round(Number(days)));
    if (n === s.days || (n !== null && !Number.isFinite(n))) return;
    void run(`/steps/${s.id}`, { days: n }, 'PATCH');
  };
  return (
    <div className="mt-2 flex items-center gap-2 text-sm">
      <span>Takes</span>
      <input className="field num w-20 py-1" inputMode="numeric" aria-label="Days it takes" value={days} onChange={(e) => setDays(e.target.value.replace(/[^\d]/g, ''))} onBlur={save} />
      <span>days</span>
      <ErrorLine error={err} />
    </div>
  );
}

function PlanDate({ projectId, step: s, expected }: { projectId: string; step: ProcessStepDto; expected: string }) {
  const [open, setOpen] = useState(false);
  const [on, setOn] = useState(s.plan?.on ?? expected);
  const [cause, setCause] = useState<'adjustment' | 'change_order'>('adjustment');
  const [reason, setReason] = useState('');
  const { busy, err, run } = useRun(projectId);
  const readOnly = useReadOnly();
  return (
    <div className="mt-2">
      {s.plan ? (
        <p className="text-sm">
          Planned {long(s.plan.on)}
          {s.plan.originalOn !== s.plan.on ? <span className={s.plan.on > s.plan.originalOn ? 'text-alert' : 'text-ink-2'}> · first planned {long(s.plan.originalOn)}</span> : null}
          {s.plan.reason && s.plan.cause !== 'baseline' ? <span className="text-ink-3">: {s.plan.reason}</span> : null}
        </p>
      ) : (
        <p className="meta">No plan recorded: it follows the work before it.</p>
      )}
      {readOnly ? null : !open ? (
        <button type="button" className="link mt-1 min-h-9 text-sm text-ink-2 hover:text-ink" onClick={() => setOpen(true)}>
          {s.plan ? 'Move the date' : 'Plan a date'}
        </button>
      ) : (
        <div className="mt-2 rounded-[2px] bg-sheet p-3">
          <div className="flex flex-wrap items-end gap-3">
            <input type="date" className="field max-w-44" aria-label="Planned for" value={on} onChange={(e) => setOn(e.target.value)} />
            {s.plan ? (
              <select className="field max-w-48" aria-label="Because of" value={cause} onChange={(e) => setCause(e.target.value as typeof cause)}>
                <option value="adjustment">An adjustment</option>
                <option value="change_order">A change order</option>
              </select>
            ) : null}
          </div>
          {s.plan ? (
            <div className="mt-2">
              <TextInput aria-label="Why it moved" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (the client sees only that it moved)" />
            </div>
          ) : (
            <p className="meta mt-2">The first date set is the plan: later moves are measured against it.</p>
          )}
          <div className="mt-2 flex gap-4">
            <Button
              tone="secondary"
              busy={busy}
              disabled={!on || (Boolean(s.plan) && !reason.trim())}
              onClick={() => void run(`/steps/${s.id}/plan`, s.plan ? { on, cause, reason: reason.trim() } : { on }).then((ok) => ok && (setOpen(false), setReason('')))}
            >
              Save the date
            </Button>
            <Button tone="quiet" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
          <ErrorLine error={err} />
        </div>
      )}
    </div>
  );
}

/** Who does it, and what it is, for NSB (design study 31, slice 3: one name for a step; its notes are in its log). */
function Words({ projectId, step: s, card = false }: { projectId: string; step: ProcessStepDto; card?: boolean }) {
  const [why, setWhy] = useState(s.why ?? '');
  const { err, run } = useRun(projectId);
  // An archived job's step reads as it ended (L-19 A): who did it; what it is reads under its title.
  if (useReadOnly()) {
    return (
      <Block label={s.trade ? 'Whose step it is' : 'Who'}>
        <p className="text-sm">{s.owner ? OWNER_WORDS[s.owner] : 'Nobody'}</p>
      </Block>
    );
  }
  // Who does it, and what it is, are on its card on the setup page (design study 25).
  if (card) return null;
  return (
    <>
      <Block label={s.trade ? 'Whose step it is' : 'Who'}>
        <select
          className="field max-w-60"
          aria-label="Who does it"
          value={s.owner ?? ''}
          onChange={(e) => void run(`/steps/${s.id}`, { owner: (e.target.value || null) as TaskOwner | null }, 'PATCH')}
        >
          <option value="">Nobody yet</option>
          {TASK_OWNERS.map((o) => (
            <option key={o} value={o}>
              {OWNER_WORDS[o]}
            </option>
          ))}
        </select>
      </Block>
      <Block label="For NSB">
        <textarea
          className="field resize-y text-sm"
          rows={2}
          aria-label="For NSB"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          onBlur={() => why.trim() !== (s.why ?? '') && void run(`/steps/${s.id}`, { why: why.trim() }, 'PATCH')}
          placeholder="What it is and why it matters, for NSB"
        />
        <ErrorLine error={err} />
      </Block>
    </>
  );
}

/**
 * What can be part of this step: the items of its own list in the tree that are not on the job (core `ownListOffers`),
 * each added in it. What comes after it is added from its phase's list (design study 31, slice 2), never here.
 */
function CanBePartOfIt({ projectId, model, stepKey, onTurnedOn }: { projectId: string; model: ProcessModel; stepKey: string; onTurnedOn?: (key: string) => void }) {
  const { busy, err, run } = useRun(projectId);
  const [name, setName] = useState<Record<string, string>>({});
  // One whose phase has closed comes late: its reason asked for under its row (design study 21, land A).
  const [late, setLate] = useState<string | null>(null);
  const next = ownListOffers(model.tree, model.process).filter((o) => o.parent === stepKey);
  if (!next.length) return null;
  return (
    <Block label={`Can be part of it (${next.length})`}>
      <ul className="space-y-1">
        {next.map((o) => (
          <li key={o.key} className="flex flex-wrap items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0">
              <span className="block break-words">{titleCase(o.title)}</span>
            </span>
            {o.repeatable ? (
              <span className="flex shrink-0 items-center gap-2">
                <input
                  className="field w-32 py-1 text-sm"
                  aria-label={`Name a new ${titleCase(o.title)}`}
                  placeholder="Its name"
                  value={name[o.key] ?? ''}
                  onChange={(e) => setName({ ...name, [o.key]: e.target.value })}
                />
                <button
                  type="button"
                  className="link min-h-9 text-ink-2 hover:text-ink disabled:opacity-40"
                  disabled={busy || !(name[o.key] ?? '').trim()}
                  onClick={() => void run('/copies', { key: o.key, name: name[o.key]!.trim() }).then((ok) => ok && (setName({ ...name, [o.key]: '' }), onTurnedOn?.(o.key)))}
                >
                  Add
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="link min-h-9 shrink-0 text-ink-2 hover:text-ink disabled:opacity-40"
                disabled={busy}
                onClick={() => (lateWords(model, o.key) ? setLate(o.key) : void run('/enable', { key: o.key }).then((ok) => ok && onTurnedOn?.(o.key)))}
              >
                Unlock
              </button>
            )}
            {late === o.key ? (
              <div className="basis-full">
                <LateReason model={model} stepKey={o.key} busy={busy} onAdd={(why) => void run('/enable', { key: o.key, reason: why }).then((ok) => ok && (setLate(null), onTurnedOn?.(o.key)))} onCancel={() => setLate(null)} />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <ErrorLine error={err} />
    </Block>
  );
}

/** Turning a step off: what goes with it, and what is kept because it has history. */
function TurnOff({ projectId, model, step: s, onDone }: { projectId: string; model: ProcessModel; step: ProcessStepDto; onDone: () => void }) {
  const [asking, setAsking] = useState(false);
  const { busy, err, run } = useRun(projectId);
  const history = new Set(model.dto.steps.filter((x) => x.hasHistory).map((x) => x.id));
  const plan = planDisable(model.process, s.id, (id) => history.has(id));
  const title = (id: string) => titleCase(model.step(id)?.title ?? '');
  if (plan.blocked) return <p className="meta border-t border-rule pt-3">{plan.blocked}</p>;
  const others = plan.off.filter((id) => id !== s.id);
  return (
    <div className="border-t border-rule pt-3">
      {!asking ? (
        <button type="button" className="link min-h-11 text-sm text-ink-3 hover:text-alert" onClick={() => setAsking(true)}>
          Turn it off
        </button>
      ) : (
        <div className="text-sm">
          <p>
            Turn off <strong className="font-medium">{titleCase(s.title)}</strong>
            {others.length ? `, and with it ${others.length} ${others.length === 1 ? 'step' : 'steps'}: ${others.slice(0, 6).map(title).join(', ')}${others.length > 6 ? '…' : ''}` : ''}?
          </p>
          {plan.keep.length ? <p className="meta mt-1">Kept, off, for their history: {plan.keep.map(title).join(', ')}</p> : null}
          <div className="mt-2 flex gap-4">
            <Button tone="danger" busy={busy} onClick={() => void run(`/steps/${s.id}/off`).then((ok) => ok && onDone())}>
              Turn off
            </Button>
            <Button tone="quiet" onClick={() => setAsking(false)}>
              Keep it
            </Button>
          </div>
        </div>
      )}
      <ErrorLine error={err} />
    </div>
  );
}
